import { ConfigProvider, App as AntdApp } from 'antd';
import { useMemo, type ReactNode } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import { useTranslation } from 'react-i18next';
import { resolveIsDark, useUiStore } from '@shared/stores/uiStore';
import { buildAntdTheme } from '@shared/design-system/antdTheme';
import { semanticDark, semanticLight } from '@shared/design-system/semantic';
import { ANTD_LOCALES, DAYJS_LOCALES } from '@/i18n/antdLocale';
import { DEFAULT_LANGUAGE, type AppLanguage } from '@/i18n/config';

interface AntdProviderProps {
  children: ReactNode;
}

export function AntdProvider({ children }: AntdProviderProps) {
  const { i18n } = useTranslation();
  const lng = (i18n.language in ANTD_LOCALES ? i18n.language : DEFAULT_LANGUAGE) as AppLanguage;
  dayjs.locale(DAYJS_LOCALES[lng]);

  const colorScheme = useUiStore((s) => s.colorScheme);
  const density = useUiStore((s) => s.density);
  const isDark = resolveIsDark(colorScheme);

  const theme = useMemo(
    () =>
      buildAntdTheme({
        semantic: isDark ? semanticDark : semanticLight,
        isDark,
        density,
      }),
    [isDark, density],
  );

  return (
    <ConfigProvider theme={theme} locale={ANTD_LOCALES[lng]} componentSize="middle">
      <AntdApp>{children}</AntdApp>
    </ConfigProvider>
  );
}
